import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SearchDropdownForm, SearchItemConfig } from "src/components/Form/search-dropdown-form";
import { CustomAIScenariosFilter } from "../ai-scenario.service";

type Props = {
  initialQuery?: CustomAIScenariosFilter;
  onApply: (filter: CustomAIScenariosFilter) => void;
};

type SearchKey = "scenarioId" | "userId";

const searchItems: Record<SearchKey, SearchItemConfig> = {
  scenarioId: {
    type: "input",
    placeholder: "Enter scenario ID",
  },
  userId: {
    type: "input",
    placeholder: "Enter user ID",
  },
};

export const AIScenariosSearch: React.FC<Props> = ({
  initialQuery = {},
  onApply,
}) => {
  const [currentFilters, setCurrentFilters] = useState<CustomAIScenariosFilter>({
    scenarioId: initialQuery.scenarioId || "",
    userId: initialQuery.userId || "",
  });
  const currentFiltersRef = useRef<CustomAIScenariosFilter>(initialQuery);

  useEffect(() => {
    const syncedFilters = {
      scenarioId: initialQuery.scenarioId || "",
      userId: initialQuery.userId || "",
    };

    setCurrentFilters(syncedFilters);
    currentFiltersRef.current = syncedFilters;
  }, [initialQuery]);

  const normalizedFilters = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(currentFilters).filter(([, value]) => typeof value === "string" && value.trim())
      ) as CustomAIScenariosFilter,
    [currentFilters]
  );

  const labels: Record<SearchKey, string> = {
    scenarioId: "Scenario ID",
    userId: "User ID",
  };

  const applyNextFilters = useCallback(
    (nextFilters: CustomAIScenariosFilter) => {
      setCurrentFilters(nextFilters);
      currentFiltersRef.current = nextFilters;
      onApply(nextFilters);
    },
    [onApply]
  );

  const handleSubmit = useCallback(
    (type: SearchKey, input: string) => {
      const nextFilters = { ...currentFiltersRef.current };

      if (input) nextFilters[type] = input;
      else delete nextFilters[type];

      applyNextFilters(nextFilters);
    },
    [applyNextFilters]
  );

  const clearFilter = (key: SearchKey) => {
    const nextFilters = { ...currentFiltersRef.current };
    delete nextFilters[key];
    applyNextFilters(nextFilters);
  };

  const clearAllFilters = () => {
    setCurrentFilters({});
    currentFiltersRef.current = {};
    onApply({});
  };

  return (
    <div className="space-y-3">
      <SearchDropdownForm<SearchKey>
        handleSubmit={handleSubmit}
        searchItems={searchItems}
      />

      {Object.keys(normalizedFilters).length > 0 && (
        <div className="flex flex-wrap items-center gap-2">
          {(Object.entries(normalizedFilters) as [SearchKey, string][]).map(([key, value]) => (
            <button
              key={`${key}-${value}`}
              onClick={() => clearFilter(key)}
              className="inline-flex items-center gap-2 rounded-full border border-cyan-200 bg-cyan-50 px-3 py-1 text-xs text-cyan-700"
              title={`Remove ${labels[key]}`}
            >
              <span className="font-medium">{labels[key]}:</span>
              <span>{value}</span>
              <span className="ml-1">×</span>
            </button>
          ))}

          <button
            type="button"
            onClick={clearAllFilters}
            className="px-3 py-1 text-xs font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors cursor-pointer"
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
};

export default AIScenariosSearch;
